import {
  createContext,
  useContext,
  useMemo,
  type HTMLAttributes,
  type LiHTMLAttributes,
  type OlHTMLAttributes,
  type ReactNode,
} from 'react';
import styled from 'styled-components';
import { colors, getMediumRGBA } from '../../../../styles/colors';
import { documentHyphenation, radii, space, text } from '../../../../styles/onboardingUiTokens';
import { SvgIcon, type SvgIconProps } from '../icons/SvgIcon';

/** Default fill for each boxed `<li>` (borderless; background only). */
const listItemBoxedBackgroundDefault = getMediumRGBA(0.06);
/** Default marker well when `iconBoxed` and the list is not `boxed`. */
const iconBoxBackgroundNotBoxedListDefault = getMediumRGBA(0.08);
/** Default marker well size when `iconBoxed`. The well centres its marker, so it never clips a larger one. */
const iconBoxSizeDefault = '2rem';
/**
 * Optical nudge for a boxed marker on a `flex-start` row. The row aligns the well with the top of the
 * text's *line box*, but the glyphs sit a little below that, so an unnudged well reads as sitting
 * high. Two pixels line the well's visual top up with the first line's letters.
 */
const iconBoxOpticalTopOffset = '2px';

export type ListIconPreset = Pick<SvgIconProps, 'name' | 'size' | 'primaryColor' | 'secondaryColor' | 'decorative'>;

export type ListOwnProps = {
  children: ReactNode;
  /** When true, renders `<ol>` with native ordered markers. `bullet`, `icon`, and `customMarkers` are ignored (see JSDoc). */
  ordered?: boolean;
  /** CSS `list-style-type` in native mode. Defaults: `disc` for `ul`, `decimal` for `ol`. */
  marker?: string;
  /**
   * Custom leading marker content (emoji, styled span, `<SvgIcon />`, …).
   * Takes precedence over `icon` when both are set on the same scope.
   * List-level default for all items unless a `ListItem` sets its own `bullet`.
   */
  bullet?: ReactNode;
  /** Renders `SvgIcon` from preset; ignored when `bullet` is set on the same scope. */
  icon?: ListIconPreset;
  /** When `true`, use the custom marker column even if list-level `bullet` / `icon` are omitted (e.g. markers only on `ListItem`). */
  customMarkers?: boolean;
  iconBoxed?: boolean;
  /** Marker well fill when `iconBoxed`. Defaults: white on a `boxed` list, light gray otherwise. */
  iconBoxBackground?: string;
  iconBoxRadius?: string;
  /** Marker well edge length when `iconBoxed` (CSS). Defaults to `2rem`. */
  iconBoxSize?: string;
  /** When true, each `<li>` is a borderless rounded tile with a light gray background by default. */
  boxed?: boolean;
  boxedRadius?: string;
  /** Per-`li` background when `boxed`. Defaults to a light gray wash. */
  boxedBackground?: string;
  rowGap?: string;
  columnGap?: string;
  listPadding?: string;
  itemPadding?: string;
  /** `<ol>` only: starting index. */
  start?: number;
  /** `<ol>` only: reversed numbering. */
  reversed?: boolean;
};

export type ListProps = ListOwnProps &
  Omit<HTMLAttributes<HTMLUListElement>, keyof ListOwnProps> &
  Partial<Pick<OlHTMLAttributes<HTMLOListElement>, 'type'>>;

export type ListItemProps = Omit<LiHTMLAttributes<HTMLLIElement>, 'title'> & {
  children: ReactNode;
  /** Visible heading above `children`, same stack pattern as `Checkbox`. Not the HTML `title` tooltip. */
  title?: ReactNode;
  /** Overrides list default `bullet` / `icon` for this row. `null` renders an empty marker cell. */
  bullet?: ReactNode | null;
  /** Per-item preset icon; `null` clears icon for this row (falls back to list `bullet` if any). */
  icon?: ListIconPreset | null;
  iconBoxed?: boolean;
  iconBoxBackground?: string;
  iconBoxRadius?: string;
  iconBoxSize?: string;
  boxedBackground?: string;
  boxedRadius?: string;
};

type ListContextValue = {
  ordered: boolean;
  customMarkerMode: boolean;
  listStyleType: string;
  listBullet: ReactNode | undefined;
  listIcon: ListIconPreset | undefined;
  iconBoxed: boolean;
  iconBoxBackground: string | undefined;
  iconBoxRadius: string | undefined;
  iconBoxSize: string | undefined;
  rowGap: string;
  columnGap: string;
  listPadding: string;
  itemPadding: string;
  boxed: boolean;
  boxedRadius: string;
  boxedBackground: string;
};

const ListContext = createContext<ListContextValue | null>(null);

function useListContext(): ListContextValue {
  const ctx = useContext(ListContext);
  if (!ctx) {
    throw new Error('`ListItem` must be used inside `List`.');
  }
  return ctx;
}

const BodyText = styled.div`
  font-size: ${text.body.fontSize};
  line-height: ${text.body.lineHeight};
  color: ${colors.dark};
  ${documentHyphenation}
`;

/** Medium (500), matching the design's H3 token — bold read far too heavy against the muted body. */
const TitleText = styled.div`
  font-size: ${text.body.fontSize};
  line-height: ${text.body.lineHeight};
  font-weight: 500;
  color: ${colors.dark};
`;

const MarkerBox = styled.span<{
  $boxed: boolean;
  $background?: string;
  $radius?: string;
  $size?: string;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-sizing: border-box;
  font-family: inherit;
  font-size: ${text.body.fontSize};
  line-height: ${text.body.lineHeight};
  min-height: ${text.body.lineHeight};
  ${({ $boxed, $background, $radius, $size }) =>
    $boxed
      ? `
    min-width: ${$size ?? iconBoxSizeDefault};
    min-height: ${$size ?? iconBoxSizeDefault};
    padding: 0;
    margin-top: ${iconBoxOpticalTopOffset};
    border-radius: ${$radius ?? radii.sm};
    background: ${$background ?? colors.white};
    border: none;
  `
      : ''}
`;

const NativeList = styled.ul<{
  $rowGap: string;
  $listPadding: string;
  $listStyleType: string;
}>`
  margin: 0;
  padding: ${({ $listPadding }) => $listPadding};
  list-style-type: ${({ $listStyleType }) => $listStyleType};
  list-style-position: outside;

  > li + li {
    margin-top: ${({ $rowGap }) => $rowGap};
  }
`;

const NativeOrderedList = styled.ol<{
  $rowGap: string;
  $listPadding: string;
  $listStyleType: string;
}>`
  margin: 0;
  padding: ${({ $listPadding }) => $listPadding};
  list-style-type: ${({ $listStyleType }) => $listStyleType};
  list-style-position: outside;

  > li + li {
    margin-top: ${({ $rowGap }) => $rowGap};
  }
`;

const CustomList = styled.ul<{
  $rowGap: string;
  $listPadding: string;
}>`
  margin: 0;
  padding: ${({ $listPadding }) => $listPadding};
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: ${({ $rowGap }) => $rowGap};
`;

const NativeLi = styled.li<{ $itemPadding: string; $boxed: boolean; $boxedRadius: string; $boxedBg: string }>`
  padding: ${({ $itemPadding }) => $itemPadding};

  ${({ $boxed, $boxedRadius, $boxedBg }) =>
    $boxed
      ? `
    border: none;
    border-radius: ${$boxedRadius};
    background-color: ${$boxedBg};
  `
      : ''}
`;

const CustomLi = styled.li<{
  $itemPadding: string;
  $columnGap: string;
  $boxed: boolean;
  $boxedRadius: string;
  $boxedBg: string;
}>`
  display: flex;
  align-items: flex-start;
  gap: ${({ $columnGap }) => $columnGap};
  padding: ${({ $itemPadding }) => $itemPadding};
  list-style: none;

  ${({ $boxed, $boxedRadius, $boxedBg }) =>
    $boxed
      ? `
    border: none;
    border-radius: ${$boxedRadius};
    background-color: ${$boxedBg};
  `
      : ''}
`;

const InnerStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

function renderIconPreset(config: ListIconPreset): ReactNode {
  return <SvgIcon {...config} />;
}

function resolveMarkerContent(args: {
  ctx: ListContextValue;
  itemBullet: ReactNode | null | undefined;
  itemIcon: ListIconPreset | null | undefined;
}): ReactNode | null {
  const { ctx, itemBullet, itemIcon } = args;

  if (itemBullet !== undefined) {
    return itemBullet === null ? null : itemBullet;
  }
  if (ctx.listBullet !== undefined) {
    return ctx.listBullet === null ? null : ctx.listBullet;
  }

  const iconFromItem = itemIcon === undefined ? ctx.listIcon : itemIcon === null ? undefined : itemIcon;
  if (iconFromItem) {
    return renderIconPreset(iconFromItem);
  }
  return null;
}

export function ListItem({
  title,
  children,
  bullet: itemBullet,
  icon: itemIcon,
  iconBoxed: itemIconBoxed,
  iconBoxBackground: itemIconBoxBackground,
  iconBoxRadius: itemIconBoxRadius,
  iconBoxSize: itemIconBoxSize,
  boxedBackground,
  boxedRadius,
  ...liRest
}: ListItemProps) {
  const ctx = useListContext();
  const hasTitle = title != null && title !== '';

  const boxedBg = boxedBackground ?? ctx.boxedBackground;
  const radius = boxedRadius ?? ctx.boxedRadius;

  const markerNode = ctx.customMarkerMode && !ctx.ordered ? resolveMarkerContent({ ctx, itemBullet, itemIcon }) : null;

  const iconBoxed = itemIconBoxed ?? ctx.iconBoxed;
  const iconBoxBgExplicit = itemIconBoxBackground ?? ctx.iconBoxBackground;
  const iconBoxRad = itemIconBoxRadius ?? ctx.iconBoxRadius;
  const iconBoxSizeResolved = itemIconBoxSize ?? ctx.iconBoxSize;
  const iconBoxBgResolved =
    iconBoxBgExplicit ?? (iconBoxed ? (ctx.boxed ? colors.white : iconBoxBackgroundNotBoxedListDefault) : undefined);

  const markerWrapped =
    markerNode == null ? null : (
      <MarkerBox $boxed={iconBoxed} $background={iconBoxBgResolved} $radius={iconBoxRad} $size={iconBoxSizeResolved}>
        {markerNode}
      </MarkerBox>
    );

  const textColumn = (
    <InnerStack>
      {hasTitle ? <TitleText>{title}</TitleText> : null}
      <BodyText>{children}</BodyText>
    </InnerStack>
  );

  if (ctx.customMarkerMode && !ctx.ordered) {
    return (
      <CustomLi
        {...liRest}
        $itemPadding={ctx.itemPadding}
        $columnGap={ctx.columnGap}
        $boxed={ctx.boxed}
        $boxedRadius={radius}
        $boxedBg={boxedBg}
      >
        {markerWrapped}
        {textColumn}
      </CustomLi>
    );
  }

  return (
    <NativeLi {...liRest} $itemPadding={ctx.itemPadding} $boxed={ctx.boxed} $boxedRadius={radius} $boxedBg={boxedBg}>
      {hasTitle ? (
        <InnerStack>
          <TitleText>{title}</TitleText>
          <BodyText>{children}</BodyText>
        </InnerStack>
      ) : (
        <BodyText>{children}</BodyText>
      )}
    </NativeLi>
  );
}

export function List(props: ListProps) {
  const {
    ordered = false,
    marker,
    bullet,
    icon,
    customMarkers,
    iconBoxed = false,
    iconBoxBackground,
    iconBoxRadius,
    iconBoxSize,
    boxed = false,
    boxedRadius = radii.lg,
    boxedBackground: boxedBackgroundProp,
    rowGap = space.md,
    columnGap = space.sm,
    listPadding,
    itemPadding = boxed ? space.sm : '0',
    start,
    reversed,
    type,
    children,
    ...rest
  } = props;

  const listStyleType = marker ?? (ordered ? 'decimal' : 'disc');

  const inferredCustom = !ordered && (bullet !== undefined || icon !== undefined || customMarkers === true);

  const customMarkerMode = ordered ? false : customMarkers === false ? false : inferredCustom;

  const listPaddingResolved =
    listPadding ?? (customMarkerMode ? '0' : ordered ? `0 0 0 ${space.lg}` : `0 0 0 ${space.lg}`);

  const boxedBackgroundResolved = boxedBackgroundProp ?? (boxed ? listItemBoxedBackgroundDefault : 'transparent');

  const ctx = useMemo<ListContextValue>(
    () => ({
      ordered,
      customMarkerMode,
      listStyleType,
      listBullet: bullet,
      listIcon: icon,
      iconBoxed,
      iconBoxBackground,
      iconBoxRadius,
      iconBoxSize,
      rowGap,
      columnGap,
      listPadding: listPaddingResolved,
      itemPadding,
      boxed,
      boxedRadius,
      boxedBackground: boxedBackgroundResolved,
    }),
    [
      ordered,
      customMarkerMode,
      listStyleType,
      bullet,
      icon,
      iconBoxed,
      iconBoxBackground,
      iconBoxRadius,
      iconBoxSize,
      rowGap,
      columnGap,
      listPaddingResolved,
      itemPadding,
      boxed,
      boxedRadius,
      boxedBackgroundResolved,
    ],
  );

  if (ordered) {
    return (
      <ListContext.Provider value={ctx}>
        <NativeOrderedList
          {...rest}
          start={start}
          reversed={reversed}
          type={type}
          $rowGap={rowGap}
          $listPadding={listPaddingResolved}
          $listStyleType={listStyleType}
        >
          {children}
        </NativeOrderedList>
      </ListContext.Provider>
    );
  }

  if (customMarkerMode) {
    return (
      <ListContext.Provider value={ctx}>
        <CustomList {...rest} $rowGap={rowGap} $listPadding={listPaddingResolved}>
          {children}
        </CustomList>
      </ListContext.Provider>
    );
  }

  return (
    <ListContext.Provider value={ctx}>
      <NativeList {...rest} $rowGap={rowGap} $listPadding={listPaddingResolved} $listStyleType={listStyleType}>
        {children}
      </NativeList>
    </ListContext.Provider>
  );
}
